(() => {
  'use strict';

  const selectors = {
    config: '[data-variant-media-groups]',
    gallery: 'product-images',
    media: '[data-image-id]',
    mediaList: '[data-product-media-list]',
    product: 'product-info',
    thumb: '[data-thumb-item]',
    thumbLink: '[data-thumb-link]',
    zoom: 'zoom-images',
    zoomImage: '[data-zoom-image]',
    zoomThumbsTemplate: '[data-pswp-thumbs-template]',
  };

  const attributes = {
    activeMedia: 'data-active-media',
    filtered: 'data-c14-media-hidden',
    imageId: 'data-image-id',
    mediaId: 'data-media-id',
    visibleCount: 'data-c14-visible-media-count',
  };

  const classes = {
    first: 'c14-media-first',
    last: 'c14-media-last',
    mediaActive: 'media--active',
  };

  const parsedConfigs = new WeakMap();

  function parseConfig(productInfo) {
    if (parsedConfigs.has(productInfo)) return parsedConfigs.get(productInfo);

    const configElement = productInfo.querySelector(selectors.config);
    let config = null;

    try {
      config = configElement ? JSON.parse(configElement.textContent) : null;
    } catch (error) {
      console.warn('C14 variant media configuration could not be parsed.', error);
    }

    parsedConfigs.set(productInfo, config);
    return config;
  }

  function getMediaItems(gallery) {
    return Array.from(gallery.querySelectorAll(selectors.media)).filter(
      (media) => media.closest(selectors.gallery) === gallery
    );
  }

  function getVisibleMediaItems(gallery) {
    return getMediaItems(gallery).filter((media) => !media.hasAttribute(attributes.filtered));
  }

  function patchGalleryNavigation() {
    const Gallery = customElements.get('product-images');
    if (!Gallery || Gallery.prototype.c14VariantMediaNavigation) return;

    Gallery.prototype.c14VariantMediaNavigation = true;

    Gallery.prototype.getNextMediaId = function () {
      const mediaItems = getVisibleMediaItems(this);
      if (!mediaItems.length) return null;

      const currentIndex = mediaItems.indexOf(this.getCurrentMedia());
      const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % mediaItems.length;
      return mediaItems[nextIndex].getAttribute(attributes.mediaId);
    };

    Gallery.prototype.getPreviousMediaId = function () {
      const mediaItems = getVisibleMediaItems(this);
      if (!mediaItems.length) return null;

      const currentIndex = mediaItems.indexOf(this.getCurrentMedia());
      const previousIndex = currentIndex <= 0 ? mediaItems.length - 1 : currentIndex - 1;
      return mediaItems[previousIndex].getAttribute(attributes.mediaId);
    };
  }

  function installZoomFilter(productInfo) {
    const zoom = productInfo.querySelector(selectors.zoom);
    if (!zoom || zoom.c14VariantMediaZoom) return;

    const originalImages = Array.from(zoom.querySelectorAll(selectors.zoomImage));
    const originalCreateZoom = zoom.createZoom.bind(zoom);
    zoom.c14VariantMediaZoom = true;

    zoom.createZoom = (originalIndex) => {
      const selectedImage = originalImages[originalIndex];
      const visibleImages = originalImages.filter(
        (image) => !image.closest(selectors.media)?.hasAttribute(attributes.filtered)
      );
      const visibleIndex = visibleImages.indexOf(selectedImage);

      if (visibleIndex < 0 || !visibleImages.length) return;

      const thumbsTemplate = productInfo.querySelector(selectors.zoomThumbsTemplate);
      const originalTemplate = thumbsTemplate?.innerHTML;

      if (thumbsTemplate && originalTemplate) {
        const templateContent = document.createElement('div');
        templateContent.innerHTML = originalTemplate;
        const allThumbs = Array.from(templateContent.children);

        if (allThumbs.length === originalImages.length) {
          const visibleIndexes = new Set(visibleImages.map((image) => originalImages.indexOf(image)));
          thumbsTemplate.innerHTML = allThumbs
            .filter((thumb, index) => visibleIndexes.has(index))
            .map((thumb) => thumb.outerHTML)
            .join('');
        }
      }

      // PhotoSwipe reads this collection again for the opening thumbnail bounds.
      zoom.images = visibleImages;

      try {
        originalCreateZoom(visibleIndex);
      } finally {
        if (thumbsTemplate && originalTemplate !== undefined) {
          thumbsTemplate.innerHTML = originalTemplate;
        }
      }
    };
  }

  function getGroups(gallery, config) {
    const mediaItems = getMediaItems(gallery);
    const mediaIndexById = new Map(
      mediaItems.map((media, index) => [String(media.getAttribute(attributes.imageId)), index])
    );
    const uniqueGroups = new Map();

    config.variants.forEach((variant) => {
      const mediaId = String(variant.mediaId || '');
      if (!mediaId || !mediaIndexById.has(mediaId) || uniqueGroups.has(mediaId)) return;

      uniqueGroups.set(mediaId, {
        mediaId,
        start: mediaIndexById.get(mediaId),
      });
    });

    const groups = Array.from(uniqueGroups.values()).sort((groupA, groupB) => groupA.start - groupB.start);

    groups.forEach((group, index) => {
      group.end = groups[index + 1]?.start ?? mediaItems.length;
    });

    return {groups, mediaItems};
  }

  function setFilteredState(element, filtered) {
    element.toggleAttribute(attributes.filtered, filtered);
    element.toggleAttribute('hidden', filtered);
    element.setAttribute('aria-hidden', String(filtered));
  }

  function filterVariantMedia(productInfo, featuredMediaId) {
    const gallery = productInfo.querySelector(selectors.gallery);
    const config = parseConfig(productInfo);

    if (!gallery || !config?.variants?.length || !featuredMediaId) return;

    const {groups, mediaItems} = getGroups(gallery, config);

    // A single media group is the normal Shop-product path and must remain unchanged.
    if (groups.length < 2) return;

    const selectedGroup = groups.find((group) => group.mediaId === String(featuredMediaId));
    if (!selectedGroup) return;

    const visibleMedia = mediaItems.slice(selectedGroup.start, selectedGroup.end);
    const visibleMediaIds = new Set(visibleMedia.map((media) => media.getAttribute(attributes.mediaId)));

    mediaItems.forEach((media) => {
      setFilteredState(media, !visibleMediaIds.has(media.getAttribute(attributes.mediaId)));
      media.classList.remove(classes.first, classes.last);
    });

    visibleMedia[0]?.classList.add(classes.first);
    visibleMedia[visibleMedia.length - 1]?.classList.add(classes.last);

    productInfo.querySelectorAll(selectors.thumb).forEach((thumb) => {
      const mediaId = thumb.querySelector(selectors.thumbLink)?.getAttribute(attributes.mediaId);
      setFilteredState(thumb, !visibleMediaIds.has(mediaId));
    });

    gallery.setAttribute(attributes.visibleCount, String(visibleMedia.length));

    const activeMedia = gallery.querySelector(`${selectors.media}.${classes.mediaActive}`);
    const selectedMedia = mediaItems[selectedGroup.start];
    const selectedMediaId = selectedMedia?.getAttribute(attributes.mediaId);

    if (!activeMedia || activeMedia.hasAttribute(attributes.filtered)) {
      gallery.setActiveMedia(selectedMediaId);
    }

    gallery.setAttribute(
      attributes.activeMedia,
      gallery.querySelector(`${selectors.media}.${classes.mediaActive}`)?.getAttribute(attributes.mediaId) || selectedMediaId
    );

    gallery.querySelectorAll('[data-button-arrow]').forEach((button) => {
      button.toggleAttribute('hidden', visibleMedia.length < 2);
    });

    installZoomFilter(productInfo);

    requestAnimationFrame(() => {
      gallery.setHeight?.();

      const isMobileSlider = window.matchMedia('(max-width: 749px)').matches && !gallery.hasAttribute('data-fader-mobile');
      if (isMobileSlider && selectedMedia) {
        gallery.querySelector(selectors.mediaList)?.scrollTo({left: selectedMedia.offsetLeft, behavior: 'auto'});
      }
    });

    productInfo.dispatchEvent(
      new CustomEvent('theme:variant-media:filtered', {
        bubbles: true,
        detail: {
          featuredMediaId: String(featuredMediaId),
          visibleMediaIds: Array.from(visibleMediaIds),
        },
      })
    );
  }

  function initializeProduct(productInfo) {
    if (!(productInfo instanceof HTMLElement)) return;

    const config = parseConfig(productInfo);
    if (!config?.currentMediaId) return;

    filterVariantMedia(productInfo, config.currentMediaId);
  }

  function initializeAll(root = document) {
    patchGalleryNavigation();
    root.querySelectorAll(selectors.product).forEach((productInfo) => {
      if (productInfo.querySelector(selectors.config)) initializeProduct(productInfo);
    });
  }

  function subscribeToVariantChanges() {
    if (window.c14VariantMediaSubscribed || typeof window.subscribe !== 'function' || !window.theme?.PUB_SUB_EVENTS?.variantChange) {
      return;
    }

    window.c14VariantMediaSubscribed = true;
    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({data}) => {
      document.querySelectorAll(selectors.product).forEach((productInfo) => {
        if (productInfo.dataset.section === String(data.sectionId)) {
          filterVariantMedia(productInfo, data.variant?.featured_media?.id);
        }
      });
    });
  }

  function initialize(root = document) {
    initializeAll(root);
    subscribeToVariantChanges();
  }

  if (!window.c14VariantMediaInitialized) {
    window.c14VariantMediaInitialized = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initialize());
    } else {
      initialize();
    }

    document.addEventListener('theme:product-info:loaded', (event) => initializeProduct(event.target));
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  }
})();
